// "Save current filter" / "Edit saved filter" modal — Polish B2 +
// visibility upgrade.

import { useEffect, useState } from "react";

import type { SavedFilterVisibility } from "../api";


export interface SaveFilterModalProps {
  open: boolean;
  /** When set, the modal renders in edit mode: pre-filled name +
   * visibility, button label flips to "Save changes". */
  initial?: {
    name: string;
    visibility: SavedFilterVisibility;
  } | null;
  isPending: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (input: {
    name: string;
    visibility: SavedFilterVisibility;
  }) => void;
}


export function SaveFilterModal({
  open, initial, isPending, error, onCancel, onSave,
}: SaveFilterModalProps) {
  const [name, setName] = useState("");
  const [visibility, setVisibility] = useState<SavedFilterVisibility>(
    "personal",
  );
  const [touched, setTouched] = useState(false);
  const editing = Boolean(initial);

  // Hydrate from ``initial`` only on open / when the *underlying*
  // values change. Without unpacking, ``initial`` is a fresh object
  // every render, which makes the effect re-run after every state
  // update and silently overwrites the radio toggle the user just
  // picked. Splitting the deps fixes the "can't change visibility
  // while editing a saved filter" bug.
  const initialName = initial?.name;
  const initialVisibility = initial?.visibility;
  useEffect(() => {
    if (!open) {
      setName("");
      setVisibility("personal");
      setTouched(false);
      return;
    }
    if (initialName !== undefined) setName(initialName);
    if (initialVisibility !== undefined) setVisibility(initialVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialName, initialVisibility]);

  if (!open) return null;
  const trimmed = name.trim();
  const blank = trimmed.length === 0;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="commitments-save-filter-title"
      data-testid="commitments-save-filter-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-text-primary/40"
    >
      <div className="w-full max-w-sm rounded-lg border border-border-subtle bg-raised p-4 shadow-lg">
        <h2
          id="commitments-save-filter-title"
          className="text-sm font-semibold text-text-primary"
        >
          {editing ? "Edit saved filter" : "Save current filter"}
        </h2>
        <p className="mt-1 text-xs text-text-secondary">
          {visibility === "personal"
            ? "Personal: only you see this filter."
            : "Broker-wide: everyone in your broker can apply this filter."}
        </p>
        <label
          className="mt-3 flex flex-col gap-1 text-xs font-medium text-text-secondary"
          htmlFor="commitments-save-filter-name"
        >
          Name
          <input
            id="commitments-save-filter-name"
            type="text"
            data-testid="commitments-save-filter-name"
            autoFocus
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!touched) setTouched(true);
            }}
            className="rounded-md border border-border-subtle bg-raised px-2 py-1 text-sm text-text-primary"
          />
        </label>
        {touched && blank && (
          <p
            data-testid="commitments-save-filter-name-error"
            className="mt-1 text-[11px] font-medium text-ember"
          >
            Name is required.
          </p>
        )}

        <fieldset className="mt-3 flex flex-col gap-1 text-xs text-text-secondary">
          <legend className="font-medium">Visibility</legend>
          <label
            className="flex items-center gap-2"
            data-testid="commitments-save-filter-visibility-personal-label"
          >
            <input
              type="radio"
              name="commitments-save-filter-visibility"
              value="personal"
              data-testid="commitments-save-filter-visibility-personal"
              checked={visibility === "personal"}
              onChange={() => setVisibility("personal")}
            />
            <span className="text-text-primary">Personal</span>
            <span className="text-text-tertiary">— only you see it</span>
          </label>
          <label
            className="flex items-center gap-2"
            data-testid="commitments-save-filter-visibility-broker-label"
          >
            <input
              type="radio"
              name="commitments-save-filter-visibility"
              value="broker"
              data-testid="commitments-save-filter-visibility-broker"
              checked={visibility === "broker"}
              onChange={() => setVisibility("broker")}
            />
            <span className="text-text-primary">Broker-wide</span>
            <span className="text-text-tertiary">— everyone in your broker</span>
          </label>
        </fieldset>

        {error && (
          <p
            data-testid="commitments-save-filter-error"
            className="mt-2 rounded-md bg-ember-tint px-2 py-1 text-[11px] text-ember"
          >
            {error}
          </p>
        )}
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            data-testid="commitments-save-filter-cancel"
            onClick={onCancel}
            className="rounded-md border border-border-subtle px-3 py-1 text-xs text-text-secondary hover:text-text-primary"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="commitments-save-filter-submit"
            disabled={blank || isPending}
            onClick={() => {
              setTouched(true);
              if (!blank) onSave({ name: trimmed, visibility });
            }}
            className="rounded-md bg-teal px-3 py-1 text-xs font-medium text-text-inverse hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isPending
              ? "Saving…"
              : editing
                ? "Save changes"
                : "Save filter"}
          </button>
        </div>
      </div>
    </div>
  );
}
